/**
 * TodayVerdictCard — iter1081
 *
 * „Един въпрос на първия екран": Как се отрази днешното хранене?
 * Една оценка, едно изречение, едно действие. Числата остават, но като
 * второстепенна информация — не като основно съдържание.
 *
 * Не прави нови заявки: ползва данните, които таблото вече е заредило.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Utensils, CheckCircle2, AlertTriangle, TrendingUp, HelpCircle } from "lucide-react";
import { dilBudgetMeaning, nisMeaning } from "@/utils/plainMeaning";

const ICONS = {
  good: CheckCircle2,
  watch: TrendingUp,
  high: AlertTriangle,
  empty: HelpCircle,
};

const TINT = {
  good: { from: "#10B981", to: "#059669" },
  watch: { from: "#F59E0B", to: "#D97706" },
  high: { from: "#EF4444", to: "#DC2626" },
  empty: { from: "#64748B", to: "#475569" },
};

export default function TodayVerdictCard({ mealDilData, dilBudget, isDark, onLogMeal }) {
  const navigate = useNavigate();
  const consumed = Number(mealDilData?.daily_effective_dil) || 0;
  const mealCount = Number(mealDilData?.meal_count) || 0;
  const budget = Number(dilBudget) || 0;

  const v = dilBudgetMeaning({ consumed, budget, mealCount });
  const Icon = ICONS[v.tone];
  const tint = TINT[v.tone];
  const nisLine = mealCount ? nisMeaning(mealDilData?.nis_daily) : null;

  const go = () => {
    if (!mealCount) {
      if (onLogMeal) return onLogMeal();
      return navigate("/dashboard/diary");
    }
    navigate("/dashboard/diary");
  };

  return (
    <div
      data-testid="today-verdict-card"
      className={`relative overflow-hidden rounded-3xl border p-4 sm:p-6 ${
        isDark ? "bg-[hsl(223,47%,11%)] border-[hsl(223,47%,20%)]" : "bg-white border-gray-100 shadow-sm"
      }`}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: `linear-gradient(90deg, ${tint.from}, ${tint.to})` }}
      />
      <p className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        Как се отрази днешното хранене
      </p>

      <div className="mt-2.5 flex items-start gap-3">
        <span
          className="w-11 h-11 shrink-0 rounded-2xl grid place-items-center"
          style={{ background: `linear-gradient(135deg, ${tint.from}, ${tint.to})` }}
        >
          <Icon className="w-5 h-5 text-white" />
        </span>
        <div className="min-w-0">
          <h2
            className={`text-base md:text-lg font-bold leading-snug ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
            data-testid="today-verdict-title"
          >
            {v.title}
          </h2>
          <p className={`mt-1 text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}
             data-testid="today-verdict-sentence">
            {v.sentence}
          </p>
          {nisLine && (
            <p className={`mt-1 text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}
               data-testid="today-verdict-nis">
              {nisLine}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={go}
          data-testid="today-verdict-action"
          className="inline-flex items-center gap-2 rounded-full px-4 min-h-[44px] text-sm font-bold text-white transition-all hover:shadow-md"
          style={{ background: `linear-gradient(90deg, ${tint.from}, ${tint.to})` }}
        >
          {!mealCount && <Utensils className="w-4 h-4" />}
          {v.action}
          <ArrowRight className="w-4 h-4" />
        </button>

        {/* Числата са второстепенни — за напредналите, не за новия човек. */}
        {budget > 0 && mealCount > 0 && (
          <span
            className={`text-[11px] tabular-nums text-right ${isDark ? "text-slate-500" : "text-slate-400"}`}
            data-testid="today-verdict-numbers"
            title="DIL е мярка колко инсулин-стимулираща храна сте приели днес."
          >
            {consumed.toFixed(0)} от {Math.round(budget)}
            <span className="block">натоварване (DIL)</span>
          </span>
        )}
      </div>
    </div>
  );
}
